import { useState, useEffect, createContext, useContext } from "react";
import {
  AssignmentApiClient,
  GroopApiClient,
  GroopItApiClient,
  InsightsApiClient,
  SlackApiClient,
  TeamsApiClient,
  UserApiClient,
  VizApiClient,
  TenantApiClient,
} from "groopit-js";
import { AIApiClient, OutlookApiClient } from "./client";

export const GroopitContext = createContext<GroopApi | undefined>(undefined);

interface GroopApi {
  user: UserApiClient;
  groop: GroopApiClient;
  assignment: AssignmentApiClient;
  groopit: GroopItApiClient;
  viz: VizApiClient;
  slack: SlackApiClient;
  teams: TeamsApiClient;
  insights: InsightsApiClient;
  ai: AIApiClient;
  outlook: OutlookApiClient;
  tenant: TenantApiClient;
  baseUrl: string;
}

//
// To be used when setting groopit API context while
// running on the groopit site.
//
export function useGroopit(baseUrl?: string): [boolean, GroopApi?] {
  // The API Clients don't like it if there's a trailing slash, though
  // it's a bit weird since they seem fine if it's _only_ a slash
  baseUrl = baseUrl?.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  return [
    false,
    {
      user: new UserApiClient({}),
      groop: new GroopApiClient({}),
      assignment: new AssignmentApiClient({}),
      groopit: new GroopItApiClient({}),
      viz: new VizApiClient({}),
      slack: new SlackApiClient({}),
      teams: new TeamsApiClient({}),
      insights: new InsightsApiClient({}),
      ai: new AIApiClient({}),
      outlook: new OutlookApiClient({}),
      tenant: new TenantApiClient({}),
      baseUrl: baseUrl ?? "/",
    },
  ];
}

//
// To be used when setting groopit api context while running
// in sharepoint
//
export function useGroopitFromSP(adToken: string): [boolean, GroopApi?] {
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState<GroopApi>();
  const baseUrl = "https://testing.groopit.co";

  useEffect(() => {
    const fetchToken = async () => {
      const response = await fetch(`${baseUrl}/connect/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
          assertion: adToken,
          scope: "openid offline_access",
        }),
      });

      const data = await response.json();
      setClient({
        user: new UserApiClient(
          { getAuthToken: () => data.access_token },
          baseUrl,
        ),
        groop: new GroopApiClient(
          { getAuthToken: () => data.access_token },
          baseUrl,
        ),
        assignment: new AssignmentApiClient(
          { getAuthToken: () => data.access_token },
          baseUrl,
        ),
        groopit: new GroopItApiClient(
          { getAuthToken: () => data.access_token },
          baseUrl,
        ),
        viz: new VizApiClient(
          { getAuthToken: () => data.access_token },
          baseUrl,
        ),
        slack: new SlackApiClient(
          { getAuthToken: () => data.access_token },
          baseUrl,
        ),
        teams: new TeamsApiClient(
          { getAuthToken: () => data.access_token },
          baseUrl,
        ),
        insights: new InsightsApiClient(
          { getAuthToken: () => data.access_token },
          baseUrl,
        ),
        ai: new AIApiClient({ getAuthToken: () => data.access_token }, baseUrl),
        outlook: new OutlookApiClient(
          { getAuthToken: () => data.access_token },
          baseUrl,
        ),
        tenant: new TenantApiClient(
          { getAuthToken: () => data.access_token },
          baseUrl,
        ),
        baseUrl,
      });
      setLoading(false);
    };

    fetchToken();
  }, [adToken, baseUrl]);

  return [loading, client];
}

export function useGroopitApiContext() {
  const context = useContext(GroopitContext);
  return (
    context ?? {
      user: new UserApiClient({}),
      groop: new GroopApiClient({}),
      assignment: new AssignmentApiClient({}),
      groopit: new GroopItApiClient({}),
      viz: new VizApiClient({}),
      slack: new SlackApiClient({}),
      teams: new TeamsApiClient({}),
      insights: new InsightsApiClient({}),
      ai: new AIApiClient({}),
      outlook: new OutlookApiClient({}),
      tenant: new TenantApiClient({}),
      baseUrl: "/",
    }
  );
}
